/* ============================================================================
   AdminMessages — /admin/messages
   ----------------------------------------------------------------------------
   iter101.32 — Full canonical grammar retrofit:
     • Overline + text-2xl title + text-xs subtitle
     • 3-tile KPI strip (Total / This week / With phone)
     • FilterSearchBar with client-side filter
     • Canonical dashed-border p-12 empty state
     • Message rows keep the existing card shape (already canonical)
   ========================================================================== */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Envelope, Phone, ChatCircle, CalendarBlank, User } from "@phosphor-icons/react";
import { FilterSearchBar } from "@/components/doctor/FilterBar";

function StatTile({ label, value, Icon, tone = "primary", testid }) {
    const accent = tone === "emerald" ? "bg-emerald-100 text-emerald-700"
        : tone === "amber" ? "bg-amber-100 text-amber-700"
        : tone === "rose" ? "bg-rose-100 text-rose-700"
        : tone === "slate" ? "bg-slate-100 text-slate-700"
        : tone === "violet" ? "bg-violet-100 text-violet-700"
        : "bg-primary/10 text-primary";
    return (
        <div className="flex items-start gap-3 rounded-xl bg-card border border-border p-3" data-testid={testid}>
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
            </div>
        </div>
    );
}

export default function AdminMessages() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/admin/messages").then((r) => setItems(r.data)).finally(() => setLoading(false));
    }, []);

    const stats = useMemo(() => {
        const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
        return {
            total: items.length,
            thisWeek: items.filter((m) => new Date(m.created_at).getTime() >= oneWeekAgo).length,
            withPhone: items.filter((m) => (m.phone || "").trim()).length,
        };
    }, [items]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return items;
        return items.filter((m) =>
            (m.name || "").toLowerCase().includes(term)
            || (m.email || "").toLowerCase().includes(term)
            || (m.phone || "").toLowerCase().includes(term)
            || (m.message || "").toLowerCase().includes(term)
        );
    }, [items, q]);

    return (
        <div className="space-y-6" data-testid="admin-messages-page">
            <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Admin · Messages</div>
                <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Contact Messages</h1>
                <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
                    Every public contact-form message — reply by email or phone from the row.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="admin-messages-kpi-strip">
                <StatTile label="Total messages" value={stats.total}     Icon={ChatCircle}    tone="primary" testid="admin-messages-kpi-total" />
                <StatTile label="This week"      value={stats.thisWeek}  Icon={CalendarBlank} tone="emerald" testid="admin-messages-kpi-week" />
                <StatTile label="With phone"     value={stats.withPhone} Icon={Phone}         tone="amber"   testid="admin-messages-kpi-phone" />
            </div>

            <FilterSearchBar
                query={q}
                setQuery={setQ}
                placeholder="Search name, email, phone or message body…"
                searchTestid="admin-messages-search"
                testid="admin-messages-search-bar"
            />

            {loading && <div className="text-muted-foreground text-sm">Loading…</div>}
            {!loading && filtered.length === 0 && (
                <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center text-muted-foreground" data-testid="admin-messages-empty">
                    <Envelope size={32} className="mx-auto mb-2 opacity-50" />
                    {items.length === 0 ? "No messages yet." : "No messages match this search."}
                </div>
            )}

            <div className="space-y-3">
                {filtered.map((m) => (
                    <div key={m.id} className="bg-card border border-border rounded-2xl p-5" data-testid={`message-${m.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold">
                                    {(m.name || "?").slice(0, 1).toUpperCase()}
                                </div>
                                <div>
                                    <div className="font-display font-semibold flex items-center gap-1.5">
                                        <User size={13} className="text-muted-foreground" />
                                        {m.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground flex items-center flex-wrap gap-x-3 mt-0.5">
                                        <a href={`mailto:${m.email}`} className="hover:text-primary flex items-center gap-1"><Envelope size={12} /> {m.email}</a>
                                        {m.phone && <span className="flex items-center gap-1"><Phone size={12} /> {m.phone}</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="text-xs text-muted-foreground tabular-nums">
                                {new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                            </div>
                        </div>
                        <p className="text-sm leading-relaxed whitespace-pre-wrap bg-secondary/40 rounded-xl p-4">{m.message}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}
